import {
  EC2Client,
  InstanceTypeInfo,
  paginateDescribeInstanceTypes,
} from "@aws-sdk/client-ec2";
import { FilterObject } from "../components/SpotInstanceTable/Filter";
import { getCacheKeyFromObject } from "./getCacheKeyFromObject";
import { getFromLocalStorage, setLocalStorage } from "../utils/localStorage";

const instanceTypeFilter =
  (filterString: string) => (instanceTypeInfo: InstanceTypeInfo) => {
    return (
      filterString === "" ||
      instanceTypeInfo.InstanceType?.includes(filterString)
    );
  };

const hibernateSupportedFilter =
  (requiresHibernate: boolean) => (instanceTypeInfo: InstanceTypeInfo) => {
    return (
      !requiresHibernate ||
      (["c3", "c4", "c5", "m4", "m5", "r3", "r4"].some(
        (family) => instanceTypeInfo.InstanceType?.indexOf(family) === 0,
      ) &&
        instanceTypeInfo.MemoryInfo?.SizeInMiB! / 1024 < 100)
    );
  };

const filterResults = (instances: InstanceTypeInfo[], filter: FilterObject) => {
  return instances
    .filter(instanceTypeFilter(filter.InstanceType))
    .filter(hibernateSupportedFilter(filter.HibernateCapableRequired));
};

export const getInstanceTypes = async (
  ec2Client: EC2Client,
  filter: FilterObject,
): Promise<InstanceTypeInfo[]> => {
  const serverSideFilters = {
    SupportedArchitecture: filter.SupportedArchitecture,
  };

  const cacheKey = `instancesTypes/${getCacheKeyFromObject(serverSideFilters)}`;
  const cachedResult = await getFromLocalStorage<InstanceTypeInfo[]>(cacheKey);

  if (cachedResult) {
    return filterResults(cachedResult, filter);
  }

  const paginator = paginateDescribeInstanceTypes(
    { client: ec2Client, pageSize: 100 },
    {
      Filters: [
        { Name: "supported-usage-class", Values: ["spot"] },
        {
          Name: "processor-info.supported-architecture",
          Values: [filter.SupportedArchitecture],
        },
      ],
    },
  );

  const instanceTypes = [];

  for await (const page of paginator) {
    if (page.InstanceTypes && page.InstanceTypes.length) {
      instanceTypes.push(...page.InstanceTypes);
    }
  }

  await setLocalStorage({
    key: cacheKey,
    value: instanceTypes,
    expiryMs: 1000 * 60 * 5,
  });

  return filterResults(instanceTypes, filter);
};
